// src/pages/ConnectionsManagement.tsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Tabs, Tab, Card, CardContent, Avatar,
  Button, Chip, Grid, CircularProgress, Alert, Paper, useTheme, alpha
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Message as MessageIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import {
  getReceivedRequests,
  getSentRequests,
  getConnections,
  acceptConnection,
  rejectConnection,
  cancelConnectionRequest,
  type Connection,
  type ConnectedUser
} from '../services/connection';
import { useAuth } from '../context/AuthContext';
import * as mentorshipApi from '../services/mentorship';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`connections-tabpanel-${index}`}
      aria-labelledby={`connections-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const ConnectionsManagement = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [receivedRequests, setReceivedRequests] = useState<Connection[]>([]);
  const [sentRequests, setSentRequests] = useState<Connection[]>([]);
  const [connections, setConnections] = useState<ConnectedUser[]>([]);
  const [mentorshipRequests, setMentorshipRequests] = useState<mentorshipApi.MentorshipRequest[]>([]);
  const [processingId, setProcessingId] = useState<string | null>(null);

  const isMember = user?.userType === 'member';
  const isExpert = user?.userType === 'expert';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [received, sent, accepted, mentorship] = await Promise.all([
        getReceivedRequests(),
        getSentRequests(),
        getConnections(),
        mentorshipApi.getMyRequests()
      ]);

      setReceivedRequests(received as Connection[]);
      setSentRequests(sent as Connection[]);
      setConnections(accepted as ConnectedUser[]);
      setMentorshipRequests(mentorship as mentorshipApi.MentorshipRequest[]);
    } catch (error) {
      console.error('Error loading connections data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (connectionId: string) => {
    setProcessingId(connectionId);
    try {
      await acceptConnection(connectionId);
      await loadData();
      alert('Connection request accepted!');
    } catch (error) {
      console.error('Error accepting connection:', error);
      alert('Failed to accept connection request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (connectionId: string) => {
    setProcessingId(connectionId);
    try {
      await rejectConnection(connectionId);
      await loadData();
      alert('Connection request rejected');
    } catch (error) {
      console.error('Error rejecting connection:', error);
      alert('Failed to reject connection request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleCancel = async (connectionId: string) => {
    setProcessingId(connectionId);
    try {
      await cancelConnectionRequest(connectionId);
      await loadData();
      alert('Connection request cancelled');
    } catch (error) {
      console.error('Error cancelling connection request:', error);
      alert('Failed to cancel connection request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleMessage = (userId: string) => {
    navigate(`/messages/${userId}`);
  };

  const handleViewProfile = (userId: string) => {
    navigate(`/members/${userId}`);
  };

  const handleAcceptMentorship = async (requestId: string) => {
    setProcessingId(requestId);
    try {
      await mentorshipApi.acceptMentorshipRequest(requestId);
      await loadData();
      alert('Mentorship request accepted! You can now message this member.');
    } catch (error) {
      console.error('Error accepting mentorship request:', error);
      alert('Failed to accept mentorship request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectMentorship = async (requestId: string) => {
    setProcessingId(requestId);
    try {
      const reason = prompt('Optional: Provide a reason for rejection');
      await mentorshipApi.rejectMentorshipRequest(requestId, reason || undefined);
      await loadData();
      alert('Mentorship request rejected');
    } catch (error) {
      console.error('Error rejecting mentorship request:', error);
      alert('Failed to reject mentorship request');
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 700 }}>
        My Connections
      </Typography>

      <Paper elevation={2} sx={{ borderRadius: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Received Requests
                {receivedRequests.length > 0 && (
                  <Chip label={receivedRequests.length} size="small" color="primary" />
                )}
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Sent Requests
                {sentRequests.length > 0 && (
                  <Chip label={sentRequests.length} size="small" color="secondary" />
                )}
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                My Connections
                {connections.length > 0 && (
                  <Chip label={connections.length} size="small" />
                )}
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isExpert ? 'Mentorship Requests' : 'My Mentorships'}
                {mentorshipRequests.filter(r => r.status === 'pending').length > 0 && (
                  <Chip
                    label={mentorshipRequests.filter(r => r.status === 'pending').length}
                    size="small"
                    color="warning"
                  />
                )}
              </Box>
            }
          />
        </Tabs>

        <Box sx={{ px: 2 }}>
          {/* Received Requests Tab */}
          <TabPanel value={tabValue} index={0}>
            {receivedRequests.length === 0 ? (
              <Alert severity="info">No pending connection requests</Alert>
            ) : (
              <Grid container spacing={3}>
                {receivedRequests.map((request) => (
                  <Grid item xs={12} md={6} key={request.id}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <Avatar
                            src={request.requester?.photoUrl || ''}
                            alt={`${request.requester?.firstName} ${request.requester?.lastName}`}
                            sx={{ width: 64, height: 64 }}
                            onClick={() => handleViewProfile(request.requesterId)}
                            style={{ cursor: 'pointer' }}
                          />
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 600, cursor: 'pointer' }}
                              onClick={() => handleViewProfile(request.requesterId)}
                            >
                              {request.requester?.firstName} {request.requester?.lastName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {request.requester?.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {request.requester?.organizationName}
                            </Typography>
                            {request.message && (
                              <Typography
                                variant="body2"
                                sx={{
                                  mt: 1,
                                  p: 1,
                                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                                  borderRadius: 1,
                                  fontStyle: 'italic'
                                }}
                              >
                                "{request.message}"
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={<CheckIcon />}
                                onClick={() => handleAccept(request.id)}
                                disabled={processingId === request.id}
                              >
                                Accept
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<CloseIcon />}
                                onClick={() => handleReject(request.id)}
                                disabled={processingId === request.id}
                              >
                                Reject
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </TabPanel>

          {/* Sent Requests Tab */}
          <TabPanel value={tabValue} index={1}>
            {sentRequests.length === 0 ? (
              <Alert severity="info">No pending sent requests</Alert>
            ) : (
              <Grid container spacing={3}>
                {sentRequests.map((request) => (
                  <Grid item xs={12} md={6} key={request.id}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <Avatar
                            src={request.receiver?.photoUrl || ''}
                            alt={`${request.receiver?.firstName} ${request.receiver?.lastName}`}
                            sx={{ width: 64, height: 64 }}
                            onClick={() => handleViewProfile(request.receiverId)}
                            style={{ cursor: 'pointer' }}
                          />
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 600, cursor: 'pointer' }}
                              onClick={() => handleViewProfile(request.receiverId)}
                            >
                              {request.receiver?.firstName} {request.receiver?.lastName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {request.receiver?.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {request.receiver?.organizationName}
                            </Typography>
                            <Chip label="Pending" size="small" color="warning" sx={{ mt: 1 }} />
                            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                              <Button
                                variant="outlined"
                                color="secondary"
                                size="small"
                                startIcon={<CancelIcon />}
                                onClick={() => handleCancel(request.id)}
                                disabled={processingId === request.id}
                              >
                                Cancel Request
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </TabPanel>

          {/* My Connections Tab */}
          <TabPanel value={tabValue} index={2}>
            {connections.length === 0 ? (
              <Alert severity="info">No connections yet. Start connecting with members!</Alert>
            ) : (
              <Grid container spacing={3}>
                {connections.map((connection) => (
                  <Grid item xs={12} md={6} key={connection.id}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <Avatar
                            src={connection.user.photoUrl || ''}
                            alt={`${connection.user.firstName} ${connection.user.lastName}`}
                            sx={{ width: 64, height: 64 }}
                            onClick={() => handleViewProfile(connection.user.id)}
                            style={{ cursor: 'pointer' }}
                          />
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 600, cursor: 'pointer' }}
                              onClick={() => handleViewProfile(connection.user.id)}
                            >
                              {connection.user.firstName} {connection.user.lastName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {connection.user.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {connection.user.organizationName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                              Connected on {new Date(connection.connectedAt).toLocaleDateString()}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={<MessageIcon />}
                                onClick={() => handleMessage(connection.user.id)}
                              >
                                Message
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleViewProfile(connection.user.id)}
                              >
                                View Profile
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </TabPanel>

          {/* Mentorship Requests Tab */}
          <TabPanel value={tabValue} index={3}>
            {mentorshipRequests.length === 0 ? (
              <Alert severity="info">
                {isExpert
                  ? 'No mentorship requests received'
                  : 'You haven\'t sent any mentorship requests yet'}
              </Alert>
            ) : (
              <Grid container spacing={3}>
                {mentorshipRequests.map((request) => {
                  const otherUser = isExpert ? request.member : request.expert;
                  if (!otherUser) return null;

                  return (
                    <Grid item xs={12} md={6} key={request.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: '100%',
                          borderLeft: 4,
                          borderLeftColor:
                            request.status === 'accepted'
                              ? 'success.main'
                              : request.status === 'rejected'
                              ? 'error.main'
                              : 'warning.main'
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Avatar
                              src={otherUser.photoUrl || ''}
                              alt={`${otherUser.firstName} ${otherUser.lastName}`}
                              sx={{ width: 64, height: 64 }}
                            />
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  {otherUser.firstName} {otherUser.lastName}
                                </Typography>
                                <Chip
                                  label={request.status}
                                  size="small"
                                  color={
                                    request.status === 'accepted'
                                      ? 'success'
                                      : request.status === 'rejected'
                                      ? 'error'
                                      : 'warning'
                                  }
                                />
                              </Box>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                {otherUser.title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                {otherUser.organizationName}
                              </Typography>

                              {/* Request Message */}
                              <Box
                                sx={{
                                  mt: 2,
                                  p: 1.5,
                                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                                  borderRadius: 1,
                                  borderLeft: 3,
                                  borderLeftColor: 'primary.main'
                                }}
                              >
                                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                  Request Message:
                                </Typography>
                                <Typography variant="body2">{request.requestMessage}</Typography>
                              </Box>

                              {request.rejectedReason && (
                                <Box
                                  sx={{
                                    mt: 1,
                                    p: 1.5,
                                    bgcolor: alpha(theme.palette.error.main, 0.05),
                                    borderRadius: 1
                                  }}
                                >
                                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                    Rejection Reason:
                                  </Typography>
                                  <Typography variant="body2">{request.rejectedReason}</Typography>
                                </Box>
                              )}

                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Requested on {new Date(request.createdAt).toLocaleDateString()}
                              </Typography>

                              {/* Action Buttons */}
                              {request.status === 'pending' && isExpert && (
                                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                  <Button
                                    variant="contained"
                                    color="success"
                                    size="small"
                                    startIcon={<CheckIcon />}
                                    onClick={() => handleAcceptMentorship(request.id)}
                                    disabled={processingId === request.id}
                                  >
                                    Accept
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    startIcon={<CloseIcon />}
                                    onClick={() => handleRejectMentorship(request.id)}
                                    disabled={processingId === request.id}
                                  >
                                    Reject
                                  </Button>
                                </Box>
                              )}

                              {request.status === 'accepted' && (
                                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    startIcon={<MessageIcon />}
                                    onClick={() => handleMessage(otherUser.id)}
                                  >
                                    Message
                                  </Button>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </TabPanel>
        </Box>
      </Paper>
    </Container>
  );
};

export default ConnectionsManagement;
